import React, { lazy } from "react";
import { Navigate } from "react-router-dom";
import MainLayout from "../layouts/MainLayout";
import ProtectedRoute from "../components/ProtectedRoute";
import PublicRoute from "../components/PublicRoute";
import RedirectToRecipe from "./RedirectToRecipe";
import RootRoute from "./RootRoute";

// ─── Member pages ────────────────────────────────────────────────────────────
const GroupPage = lazy(() => import("../pages/member/GroupPage"));
const GroupDetailsPage = lazy(() => import("../pages/member/GroupDetailPage"));
const SearchRfpsPage = lazy(() => import("../pages/member/SearchRfpsPage"));
const ReportPage = lazy(() => import("../pages/member/ReportPage"));
const FeedbackReportsPage = lazy(() => import("../pages/member/FeedbackReportsPage"));
const UserProfilePage = lazy(() => import("../pages/member/UserProfilePage"));
const RfpExpandedPage = lazy(() => import("../pages/member/ExpandedRowPage"));
const TopRfpsPage = lazy(() => import("../pages/member/TopRfpsPage"));
const BillingInfoPage = lazy(() => import("../pages/member/BillingInfoPage"));
const VerifiedPage = lazy(() => import("../pages/member/VerifiedPage"));
const NotFoundPage = lazy(() => import("../pages/NotFoundPage"));

// ─── Admin pages ─────────────────────────────────────────────────────────────
const AdminDashboardPage = lazy(() => import("../pages/company-admin/AdminDashboardPage"));
const AdminControlPannel = lazy(() => import("../pages/company-admin/AdminControlPannel"));
const CompanyAnalyticsPage = lazy(() => import("../pages/company-admin/CompanyAnalyticsPage"));
const BillingPage = lazy(() => import("../pages/company-admin/Billing"));
const BillingHistory = lazy(() => import("../features/billing/BillingHistoryPage"));

// ─── Super admin pages ────────────────────────────────────────────────────────
const SuperAdminDashboard = lazy(() => import("../pages/super-admin/SuperAdminDashboard"));

// ─── Public pages ─────────────────────────────────────────────────────────────
const LoginPage = lazy(() => import("../pages/member/LoginPage"));
const CompanyRegistrationPage = lazy(() => import("../pages/member/Signup"));
const ForgotPasswordPage = lazy(() => import("../pages/member/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("../pages/member/ResetPasswordPage"));
const VerifyEmailNoticePage = lazy(() => import("../pages/member/VerifyEmailPage"));
const VerifyEmailPage = lazy(() => import("../pages/member/VerifyEmailPage"));
const AcceptInvitationPage = lazy(() => import("../pages/member/AcceptInvitationPage"));
const CompanyInvitationPage = lazy(() => import("../pages/member/CompanyInvitationPage"));
const PaymentPage = lazy(() => import("../pages/member/PaymentPage"));
const BillingSuccessPage = lazy(() => import("../pages/member/BillingSucessPage"));
const FeedbackSubmissionPage = lazy(() => import("../pages/member/FeedbackSubmissionPage"));
const PricingPage = lazy(() => import("../pages/member/Pricing"));
const PrivacyPage = lazy(() => import("../pages/member/PrivacyPage"));
const TermsPage = lazy(() => import("../pages/member/TermsPage"));
const ContactUsPage = lazy(() => import("../pages/member/ContactUs"));
const FAQPage = lazy(() => import("../pages/member/FAQPage"));
const TargetingTemplatesPage = lazy(() => import("../pages/member/TargetingTemplatesPage"));
const TargetingTemplateDetailPage = lazy(() => import("../pages/member/TargetingTemplateDetailPage"));

// ─────────────────────────────────────────────────────────────────────────────

const memberRoutes = [
  { path: "/groups", element: <GroupPage /> },
  { path: "/groups/:groupId", element: <GroupDetailsPage /> },
  { path: "/recipes", element: <TargetingTemplatesPage /> },
  { path: "/recipes/:templateId", element: <TargetingTemplateDetailPage /> },
  { path: "/targeting", element: <Navigate to="/recipes" replace /> },
  { path: "/targeting/:templateId", element: <RedirectToRecipe /> },
  { path: "/search-rfps", element: <SearchRfpsPage /> },
  { path: "/report/:id", element: <ReportPage /> },
  { path: "/targeting-logic", element: <Navigate to="/recipes" replace /> },
  { path: "/feedback-reports", element: <FeedbackReportsPage /> },
  { path: "/user-profile", element: <UserProfilePage /> },
  { path: "/rfp/expanded/:source/:id", element: <RfpExpandedPage /> },
  { path: "/rfp/expanded/:id", element: <RfpExpandedPage /> },
  { path: "/top-rfps", element: <TopRfpsPage /> },
  { path: "/billing", element: <BillingInfoPage /> },
];

const adminRoutes = [
  { path: "/admin", element: <AdminDashboardPage /> },
  { path: "/admin/analytics", element: <CompanyAnalyticsPage /> },
  { path: "/admin/billing", element: <BillingPage /> },
  { path: "/admin/billing/history", element: <BillingHistory /> },
  { path: "/admin/control-pannel", element: <AdminControlPannel /> },
];

const superAdminRoutes = [
  { path: "/super-admin", element: <SuperAdminDashboard /> },
  { path: "/super-admin/billing", element: <BillingPage /> },
];

const publicRoutes = [
  { path: "/login", element: <LoginPage /> },
  { path: "/signup", element: <CompanyRegistrationPage /> },
  { path: "/pricing", element: <PricingPage /> },
  { path: "/privacy", element: <PrivacyPage /> },
  { path: "/terms", element: <TermsPage /> },
  { path: "/contact-us", element: <ContactUsPage /> },
  { path: "/faq", element: <FAQPage /> },
  { path: "/payment", element: <PaymentPage /> },
  { path: "/verify-email-notice", element: <VerifyEmailNoticePage /> },
  { path: "/verify-email/:token", element: <VerifyEmailPage /> },
  { path: "/accept-invitation/:token", element: <AcceptInvitationPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/reset-password/:token", element: <ResetPasswordPage /> },
];

// Routes outside layout wrappers (no Navbar/Footer, no auth guard)
const standaloneRoutes = [
  { path: "/company-invitation/:token", element: <CompanyInvitationPage /> },
  { path: "/billing/success", element: <BillingSuccessPage /> },
  { path: "/verified", element: <VerifiedPage /> },
  { path: "/feedback-submit", element: <FeedbackSubmissionPage /> },
  { path: "/ai-usage", element: <Navigate to="/not-found" replace /> },
  { path: "/main", element: <Navigate to="/" replace /> },
  { path: "/not-found", element: <NotFoundPage /> },
  { path: "*", element: <NotFoundPage /> },
];

export const routes = [
  // Standalone — no layout, no auth
  ...standaloneRoutes,

  { path: "/", element: <RootRoute /> },

  // Public — no auth required (redirect if logged in)
  {
    element: <PublicRoute />,
    children: publicRoutes,
  },

  // Protected — auth required, with Navbar + Footer
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <MainLayout />,
        children: [
          ...memberRoutes,
          ...adminRoutes,
          ...superAdminRoutes,
        ],
      },
    ],
  },
];
